/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import { FunctionComponent } from "react";

import { StrapiFeatureTable } from "@/types/strapi";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { Spacing } from "@/tokens/spacing";

import { Button } from "@/ui/atoms/button";
import { Flag } from "@/ui/atoms/flag";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { SubgridContainer } from "@/ui/atoms/subgrid_container";
import { Text } from "@/ui/atoms/text";
import { RichText } from "@/ui/molecules/rich_text";

import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { buildStylesByBreakpoint } from "@/util/style_util";
import { convertToRem } from "@/util/ui_util";

interface AllFeaturesTablesProps {
    allFeaturesTables: StrapiFeatureTable[];
    className?: SerializedStyles;
}

export const AllFeaturesTables: FunctionComponent<AllFeaturesTablesProps> = (
    props,
) => {
    /**
     * Hooks
     */
    const theme = useTypedTheme();

    /**
     * Styles
     */
    const rowStyles = css({
        alignItems: "center",
        borderTop: `1px solid ${Colors[theme.ui.uiBorder]}`,
        paddingBottom: Spacing["spacing-6"],
        paddingTop: Spacing["spacing-6"],
    });

    const tableMargin = buildStylesByBreakpoint("marginTop", {
        extraSmall: Spacing["spacing-16"],
        large: Spacing["spacing-24"],
    });

    const rowTitleBlockStyles = buildStylesByBreakpoint("marginBottom", {
        extraSmall: Spacing["spacing-2"],
        medium: "auto",
    });

    const rowTitleFlagStyles = css({
        borderRadius: BorderRadiuses["borderSmall"],
        boxShadow: `inset 0 0 0 1px ${Colors["lighten-20"]}`,
        display: "inline-flex",
        fontWeight: 400,
        padding: `${convertToRem(4)} ${convertToRem(6)}`,
    });

    /**
     * Rendering
     */
    const renderTableRows = (table: StrapiFeatureTable) => (
        <SubgridContainer
            className={css({
                marginTop: Spacing["spacing-12"],
            })}
            columnSpan={{
                extraSmall: 6,
                medium: 12,
                large: 8,
            }}
            columnStart={{
                extraSmall: 1,
                large: 3,
            }}
            tag="ul"
        >
            {table.Table_Rows.map((row) => {
                const rowTitleTextStyles = css({
                    display: "inline",
                    marginRight: row.Flag ? convertToRem(12) : undefined,
                });

                return (
                    <SubgridContainer
                        className={rowStyles}
                        columnSpan={{
                            extraSmall: "full",
                            medium: 12,
                            large: 8,
                        }}
                        columnStart={1}
                        key={`all-features::row::${table.id}-${row.id}`}
                        tag="li"
                    >
                        <GridColumn
                            className={rowTitleBlockStyles}
                            columnSpan={{
                                extraSmall: "full",
                                medium: 4,
                                large: 3,
                            }}
                        >
                            <Text
                                className={rowTitleTextStyles}
                                fontSize="AllFeaturesTable"
                                fontWeight="medium"
                                tag="h3"
                            >
                                {row.Title}
                            </Text>

                            {row.Flag && (
                                <Flag
                                    className={rowTitleFlagStyles}
                                    color="lighten-80"
                                    fontSize="TEXT_075"
                                >
                                    {row.Flag}
                                </Flag>
                            )}
                        </GridColumn>

                        <GridColumn
                            className={css({
                                // We should apply this rule in the serializer. First element should not have margin
                                "& > *:first-of-type": { marginTop: "0" },
                            })}
                            columnSpan={{
                                extraSmall: "full",
                                medium: 8,
                                large: 5,
                            }}
                            columnStart={{
                                extraSmall: 1,
                                medium: 5,
                                large: 4,
                            }}
                        >
                            <RichText config="Longform">
                                {row.Description}
                            </RichText>
                        </GridColumn>
                    </SubgridContainer>
                );
            })}

            <SubgridContainer
                className={css({
                    display: "flex",
                    justifyContent: "flex-end",
                })}
                columnSpan={{
                    extraSmall: 6,
                    medium: 12,
                    large: 8,
                }}
                columnStart={1}
                tag="li"
            >
                {table.Link && (
                    <Button
                        fontSize="AllFeaturesTableButton"
                        href={table.Link?.URL}
                        iconSlug="Arrow Right"
                        marginTop="Default"
                        variant="secondary"
                    >
                        {table.Link?.Text}
                    </Button>
                )}
            </SubgridContainer>
        </SubgridContainer>
    );

    return (
        <div
            css={css({
                borderTop: `1px solid ${Colors[theme.ui.uiBorder]}`,
            })}
        >
            {props.allFeaturesTables.map((_table) => {
                return (
                    <GridContainer
                        className={tableMargin}
                        key={`all-features::table::${_table.id}`}
                        tag="section"
                    >
                        <Text
                            columnSpan={{
                                extraSmall: "full",
                                medium: 8,
                            }}
                            columnStart={{
                                extraSmall: 1,
                                large: 3,
                            }}
                            fontSize="AllFeaturesTableHeadline"
                            tag="h2"
                        >
                            {_table.Title}
                        </Text>

                        {renderTableRows(_table)}
                    </GridContainer>
                );
            })}
        </div>
    );
};
